/**
 * Robust range-splitting format engine. Replaces document.execCommand for
 * bold/italic/link formatting. Handles cross-element selections without
 * using range.surroundContents() (which throws on partial selections).
 *
 * This module is used in two ways:
 * 1. Imported by iframe-bridge.js and serialized into the iframe script
 * 2. Can be tested standalone in Node with jsdom
 */

const INLINE_TEXT_COLOR_CLASS_BY_TOKEN = Object.freeze({
  accent: 'pb-inline-color-accent',
  heading: 'pb-inline-color-heading',
  body: 'pb-inline-color-body'
});
const INLINE_TEXT_COLOR_TOKENS = Object.keys(INLINE_TEXT_COLOR_CLASS_BY_TOKEN);
const INLINE_TEXT_COLOR_CLASSES = Object.values(INLINE_TEXT_COLOR_CLASS_BY_TOKEN);

/**
 * Apply or remove a format tag (strong, em) on the current selection range.
 * Toggles: if the entire selection is already wrapped, unwraps it.
 *
 * @param {string} tag - 'strong' or 'em'
 * @param {Range} range - The current selection range
 * @param {Document} doc - The document context (iframe document)
 * @returns {boolean} Whether format was applied (true) or removed (false)
 */
export function toggleFormat(tag, range, doc) {
  if (range.collapsed) return false;

  const marked = markRange(range, doc);
  const workingRange = marked?.range || range;
  const tagUpper = tag.toUpperCase();
  const altTag = tagUpper === 'STRONG' ? 'B' : tagUpper === 'EM' ? 'I' : null;

  if (isFullyWrapped(workingRange, tagUpper, altTag)) {
    if (marked) unwrapMarkedFormat(marked, tagUpper, altTag, doc);
    else unwrapFormat(workingRange, tagUpper, altTag, doc);
    restoreMarkedRange(marked, doc);
    return false;
  }

  applyFormat(tag, workingRange, doc);
  restoreMarkedRange(marked, doc);
  return true;
}

/**
 * Apply a link to the current selection.
 * @param {string} href - The URL
 * @param {Range} range - The current selection range
 * @param {Document} doc - The document context
 * @param {{ openInNewTab?: boolean }} [options]
 */
export function applyLink(href, range, doc, options = {}) {
  if (range.collapsed) return;

  const openInNewTab = options.openInNewTab !== false;

  const active = getActiveLink(range);
  if (active?.element) {
    const anchor = active.element;
    if (anchor.contains(range.startContainer) && anchor.contains(range.endContainer)) {
      anchor.setAttribute('href', href);
      if (openInNewTab) {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      } else {
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
      }
      const sel = doc.getSelection();
      sel.removeAllRanges();
      const newRange = doc.createRange();
      newRange.selectNodeContents(anchor);
      sel.addRange(newRange);
      return;
    }
  }

  removeLinksInRange(range, doc);

  const fragment = range.extractContents();
  const anchor = doc.createElement('a');
  anchor.href = href;
  if (openInNewTab) {
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
  }
  anchor.appendChild(fragment);
  range.insertNode(anchor);

  const sel = doc.getSelection();
  sel.removeAllRanges();
  const newRange = doc.createRange();
  newRange.selectNodeContents(anchor);
  sel.addRange(newRange);
}

/**
 * Remove all links from the current selection range, preserving their content.
 * @param {Range} range - The current selection range
 * @param {Document} doc - The document context
 */
export function removeLink(range, doc) {
  removeLinksInRange(range, doc);
}

/**
 * Apply or toggle an inline text color class on the current selection.
 * Colors are mutually exclusive: applying one removes the others.
 *
 * @param {'accent'|'heading'|'body'} colorToken
 * @param {Range} range
 * @param {Document} doc
 * @returns {boolean} true when applied, false when removed/toggled off
 */
export function applyTextColor(colorToken, range, doc) {
  if (!range || range.collapsed) return false;
  const className = INLINE_TEXT_COLOR_CLASS_BY_TOKEN[colorToken];
  if (!className) return false;

  const marked = markRange(range, doc);
  const workingRange = marked?.range || range;
  const wasAlreadyActive = isFullyClassWrapped(workingRange, className);
  removeClassesInRange(workingRange, INLINE_TEXT_COLOR_CLASSES, doc);
  if (wasAlreadyActive) {
    restoreMarkedRange(marked, doc);
    return false;
  }

  applyClassFormat(className, workingRange, doc);
  restoreMarkedRange(marked, doc);
  return true;
}

/**
 * Returns the active inline text color token when the full selection shares
 * a single color class; otherwise returns null.
 *
 * @param {Range} range
 * @returns {'accent'|'heading'|'body'|null}
 */
export function getActiveTextColor(range) {
  if (!range || range.collapsed) return null;
  for (const token of INLINE_TEXT_COLOR_TOKENS) {
    if (isFullyClassWrapped(range, INLINE_TEXT_COLOR_CLASS_BY_TOKEN[token])) {
      return token;
    }
  }
  return null;
}

/**
 * Apply, swap, or remove a pb-font-<slug> inline-font wrapper on the current
 * selection. The slug list isn't enumerated here (the catalog lives in the
 * parent). Empty slug → unwrap only ("Default" option in the picker).
 *
 * Two execution paths:
 *
 * 1. Fast path — range covers ALL of one pb-font-* span. Swap the class on
 *    the existing span in place (or unwrap it for empty slug). No DOM
 *    mutation that would invalidate any caller-held range.
 *
 * 2. Partial path — selection lives inside a pb-font-* span but does not
 *    cover its full content, OR spans multiple nodes. Use extractContents
 *    + marker-guided lifting so the original span splits around the selected
 *    text instead of nesting one pb-font-* class inside another. Strips any
 *    nested pb-font-* classes from the extracted fragment so only the
 *    outermost class wins.
 *
 * @param {string} slug - e.g. "inter" or "" to unwrap.
 * @param {Range} range
 * @param {Document} doc
 * @returns {boolean} true when a new class was applied, false otherwise.
 */
export function applyFontFamily(slug, range, doc) {
  if (!range || range.collapsed) return false;
  const rawSlug = String(slug || '')
    .trim()
    .toLowerCase();
  const safeSlug = rawSlug.replace(/[^a-z0-9-]/g, '');
  if (safeSlug !== rawSlug) return false;

  // If the range covers the nearest font span's entire content, swap class in
  // place. Partial selections go through marker-guided lifting below so the
  // original span splits around the selected text.
  const enclosing = _findEnclosingPbFontSpan(range);
  if (enclosing && _rangeCoversEntireText(range, enclosing)) {
    _stripPbFontClasses(enclosing);
    if (safeSlug) {
      enclosing.classList.add('pb-font-' + safeSlug);
      return true;
    }
    _unwrapClasslessSpan(enclosing);
    return false;
  }

  // Partial / multi-node path: extract, strip nested classes, wrap, reinsert.
  const fragment = range.extractContents();
  if (!fragment || fragment.childNodes.length === 0) return false;
  _stripPbFontClassesInFragment(fragment);

  const startMarker = doc.createComment('pb-font-start');
  const endMarker = doc.createComment('pb-font-end');
  range.insertNode(endMarker);
  endMarker.parentNode.insertBefore(startMarker, endMarker);

  if (safeSlug) {
    const inserted = doc.createElement('span');
    inserted.className = 'pb-font-' + safeSlug;
    inserted.appendChild(fragment);
    endMarker.parentNode.insertBefore(inserted, endMarker);
  } else {
    endMarker.parentNode.insertBefore(fragment, endMarker);
  }

  _liftMarkedFontContent(startMarker, endMarker, doc);
  return !!safeSlug;
}

function _findEnclosingPbFontSpan(range) {
  let node = range.commonAncestorContainer;
  if (node.nodeType === 3) node = node.parentNode;
  while (node) {
    if (node.nodeType === 1 && node.tagName === 'SPAN' && node.classList) {
      for (const cls of node.classList) {
        if (cls.startsWith('pb-font-')) return node;
      }
    }
    node = node.parentNode;
  }
  return null;
}

function _rangeCoversEntireText(range, node) {
  // String comparison is robust across DOM mutations and across implementations
  // (JSDOM returns 1 for compareBoundaryPoints((text,0), (span,0)) even though
  // they refer to the same tree position).
  return range.toString() === node.textContent;
}

function _stripPbFontClasses(el) {
  if (!el.classList) return;
  const toRemove = [];
  for (const cls of el.classList) {
    if (cls.startsWith('pb-font-')) toRemove.push(cls);
  }
  for (const cls of toRemove) el.classList.remove(cls);
  if (el.classList.length === 0) el.removeAttribute('class');
}

function _unwrapClasslessSpan(el) {
  if (!el || el.tagName !== 'SPAN' || el.attributes.length !== 0) return;
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

function _hasPbFontClass(el) {
  if (!el || !el.classList) return false;
  for (const cls of el.classList) {
    if (cls.startsWith('pb-font-')) return true;
  }
  return false;
}

function _stripPbFontClassesInFragment(fragment) {
  const elements = fragment.querySelectorAll ? Array.from(fragment.querySelectorAll('*')) : [];
  for (let i = elements.length - 1; i >= 0; i--) {
    _stripPbFontClasses(elements[i]);
    _unwrapClasslessSpan(elements[i]);
  }
}

function _findDirectMarkedFontAncestor(startMarker, endMarker) {
  let current = startMarker.parentNode;
  while (current && current.nodeType === 1) {
    if (
      _hasPbFontClass(current) &&
      startMarker.parentNode === current &&
      endMarker.parentNode === current
    ) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
}

function _removeIfEmptyElement(el) {
  if (!el || !el.parentNode || el.childNodes.length !== 0) return;
  el.parentNode.removeChild(el);
}

function _liftMarkedFontContent(startMarker, endMarker, doc) {
  const fontAncestor = _findDirectMarkedFontAncestor(startMarker, endMarker);
  if (!fontAncestor || !fontAncestor.parentNode) {
    if (startMarker.parentNode) startMarker.parentNode.removeChild(startMarker);
    if (endMarker.parentNode) endMarker.parentNode.removeChild(endMarker);
    return;
  }

  const parent = fontAncestor.parentNode;
  const afterClone = fontAncestor.cloneNode(false);
  let node = endMarker.nextSibling;
  while (node) {
    const next = node.nextSibling;
    afterClone.appendChild(node);
    node = next;
  }

  const lifted = doc.createDocumentFragment();
  node = startMarker.nextSibling;
  while (node && node !== endMarker) {
    const next = node.nextSibling;
    lifted.appendChild(node);
    node = next;
  }

  if (startMarker.parentNode) startMarker.parentNode.removeChild(startMarker);
  if (endMarker.parentNode) endMarker.parentNode.removeChild(endMarker);

  const beforeNext = fontAncestor.nextSibling;
  if (lifted.childNodes.length) parent.insertBefore(lifted, beforeNext);
  if (afterClone.childNodes.length) parent.insertBefore(afterClone, beforeNext);
  _removeIfEmptyElement(fontAncestor);
}

/**
 * Returns the active pb-font slug when every text node in the selection sits
 * inside the same pb-font-* class; null when the selection has none or has
 * mixed font classes.
 */
export function getActiveFontSlug(range) {
  if (!range || range.collapsed) return null;
  const nodes = getTextNodesInRange(range);
  if (nodes.length === 0) return null;
  let active = null;
  for (const node of nodes) {
    const slug = closestFontSlug(node, range.commonAncestorContainer);
    if (!slug) return null;
    if (active == null) active = slug;
    else if (active !== slug) return null;
  }
  return active;
}

function closestFontSlug(node, boundary) {
  let current = node.parentNode;
  while (current) {
    if (current.nodeType === 1 && current.classList) {
      for (const cls of current.classList) {
        if (cls.startsWith('pb-font-')) return cls.slice('pb-font-'.length);
      }
    }
    if (current === boundary || current === boundary.parentNode) break;
    current = current.parentNode;
  }
  return null;
}

/**
 * Check if the current selection is inside a link.
 * @param {Range} range - The current selection range
 * @returns {{ href: string, target: string, rel: string, element: Element } | null}
 */
export function getActiveLink(range) {
  let node = range.commonAncestorContainer;
  if (node.nodeType === 3) node = node.parentNode;
  const anchor = node.closest ? node.closest('a') : null;
  if (anchor) {
    return {
      href: anchor.getAttribute('href') || anchor.href,
      target: anchor.getAttribute('target') || '',
      rel: anchor.getAttribute('rel') || '',
      element: anchor
    };
  }
  return null;
}

/**
 * Check whether the given format tag is active across the entire range.
 * @param {Range} range
 * @param {string} tag - e.g. 'STRONG', 'EM'
 * @returns {boolean}
 */
export function isFormatActive(range, tag) {
  const altTag = tag === 'STRONG' ? 'B' : tag === 'EM' ? 'I' : null;
  return isFullyWrapped(range, tag, altTag);
}

// --- Internal helpers ---

function markRange(range, doc) {
  if (!range || range.collapsed) return null;
  const start = doc.createComment('pb-format-start');
  const end = doc.createComment('pb-format-end');
  const endPoint = range.cloneRange();
  endPoint.collapse(false);
  endPoint.insertNode(end);
  const startPoint = range.cloneRange();
  startPoint.collapse(true);
  startPoint.insertNode(start);
  const workingRange = doc.createRange();
  workingRange.setStartAfter(start);
  workingRange.setEndBefore(end);
  return { start, end, range: workingRange };
}

function restoreMarkedRange(marked, doc) {
  if (!marked?.start?.parentNode || !marked?.end?.parentNode) return null;
  const next = doc.createRange();
  next.setStartAfter(marked.start);
  next.setEndBefore(marked.end);
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(next);
  marked.start.remove();
  marked.end.remove();
  return selection?.rangeCount ? selection.getRangeAt(0) : next;
}

function firstTextDescendant(node) {
  let current = node;
  while (current && current.nodeType !== 3) current = current.firstChild;
  return current?.nodeType === 3 ? current : null;
}

function lastTextDescendant(node) {
  let current = node;
  while (current && current.nodeType !== 3) current = current.lastChild;
  return current?.nodeType === 3 ? current : null;
}

function isWordCharacter(char) {
  if (!char) return false;
  return (
    char === '_' || /[0-9]/.test(char) || char.toLocaleLowerCase() !== char.toLocaleUpperCase()
  );
}

export function expandRangeToWord(range, editableRoot) {
  if (!range || !range.collapsed || !editableRoot) return range;
  let textNode = range.startContainer.nodeType === 3 ? range.startContainer : null;
  let offset = range.startOffset;
  if (!textNode) {
    const container = range.startContainer;
    const childOffset = offset;
    textNode = firstTextDescendant(container.childNodes?.[childOffset] || null);
    offset = 0;
    if (!textNode && childOffset > 0) {
      textNode = lastTextDescendant(container.childNodes?.[childOffset - 1] || null);
      offset = textNode?.nodeValue?.length || 0;
    }
  }
  if (!textNode || !editableRoot.contains(textNode)) return range;
  const value = textNode.nodeValue || '';
  if (offset === value.length && offset > 0 && isWordCharacter(value[offset - 1])) offset -= 1;
  if (!isWordCharacter(value[offset])) return range;
  let start = offset;
  let end = offset + 1;
  while (start > 0 && isWordCharacter(value[start - 1])) start -= 1;
  while (end < value.length && isWordCharacter(value[end])) end += 1;
  const expanded = range.cloneRange();
  expanded.setStart(textNode, start);
  expanded.setEnd(textNode, end);
  return expanded;
}

function isFullyWrapped(range, tagUpper, altTag) {
  const contents = getTextNodesInRange(range);
  if (contents.length === 0) return false;

  for (const textNode of contents) {
    if (!hasAncestorTag(textNode, tagUpper, altTag, range.commonAncestorContainer)) {
      return false;
    }
  }
  return true;
}

function hasAncestorTag(node, tagUpper, altTag, boundary) {
  let current = node.parentNode;
  while (current) {
    if (current.nodeType === 1) {
      const name = current.tagName;
      if (name === tagUpper || (altTag && name === altTag)) return true;
    }
    if (current === boundary || current === boundary.parentNode) break;
    current = current.parentNode;
  }
  return false;
}

function hasAncestorClass(node, className, boundary) {
  let current = node.parentNode;
  while (current) {
    if (current.nodeType === 1 && current.classList && current.classList.contains(className)) {
      return true;
    }
    if (current === boundary || current === boundary.parentNode) break;
    current = current.parentNode;
  }
  return false;
}

function getTextNodesInRange(range) {
  const nodes = [];
  const walker = document.createTreeWalker(
    range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (node.textContent.length === 0) return NodeFilter.FILTER_REJECT;
        const nodeRange = document.createRange();
        nodeRange.selectNodeContents(node);
        if (range.compareBoundaryPoints(Range.END_TO_START, nodeRange) >= 0)
          return NodeFilter.FILTER_REJECT;
        if (range.compareBoundaryPoints(Range.START_TO_END, nodeRange) <= 0)
          return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );

  let node;
  while ((node = walker.nextNode())) {
    nodes.push(node);
  }
  return nodes;
}

function applyFormat(tag, range, doc) {
  const textNodes = getTextNodesInRange(range);
  if (textNodes.length === 0) return;

  // For single text node that's fully selected or a simple case
  if (textNodes.length === 1) {
    if (
      !hasAncestorTag(
        textNodes[0],
        tag.toUpperCase(),
        tag === 'strong' ? 'B' : tag === 'em' ? 'I' : null,
        range.commonAncestorContainer
      )
    ) {
      wrapTextNodeRange(textNodes[0], range, tag, doc);
    }
    mergeEquivalentWrappers(range.commonAncestorContainer, tag);
    return;
  }

  // Multiple text nodes: wrap each one's intersection with the range
  // Process in reverse so earlier insertions don't shift later offsets
  for (let i = textNodes.length - 1; i >= 0; i--) {
    if (
      !hasAncestorTag(
        textNodes[i],
        tag.toUpperCase(),
        tag === 'strong' ? 'B' : tag === 'em' ? 'I' : null,
        range.commonAncestorContainer
      )
    ) {
      wrapTextNodeRange(textNodes[i], range, tag, doc);
    }
  }
  mergeEquivalentWrappers(range.commonAncestorContainer, tag);
}

function mergeEquivalentWrappers(root, tag) {
  const boundary = root?.nodeType === 1 ? root : root?.parentElement;
  if (!boundary?.querySelectorAll) return;
  const wrappers = Array.from(boundary.querySelectorAll(tag));
  for (let i = wrappers.length - 1; i >= 0; i--) {
    const wrapper = wrappers[i];
    const parentSame = wrapper.parentElement?.tagName === tag.toUpperCase();
    if (parentSame && wrapper.attributes.length === 0) {
      while (wrapper.firstChild) wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
      wrapper.remove();
    }
  }
  for (const wrapper of Array.from(boundary.querySelectorAll(tag))) {
    let next = wrapper.nextSibling;
    while (next?.nodeType === 3 && next.nodeValue === '') next = next.nextSibling;
    if (
      next?.nodeType === 1 &&
      next.tagName === wrapper.tagName &&
      wrapper.attributes.length === 0 &&
      next.attributes.length === 0
    ) {
      while (next.firstChild) wrapper.appendChild(next.firstChild);
      next.remove();
    }
  }
}

function applyClassFormat(className, range, doc) {
  const textNodes = getTextNodesInRange(range);
  if (textNodes.length === 0) return;

  for (let i = textNodes.length - 1; i >= 0; i--) {
    wrapTextNodeRangeWithClass(textNodes[i], range, className, doc);
  }
}

function wrapTextNodeRange(textNode, range, tag, doc) {
  const nodeRange = doc.createRange();
  nodeRange.selectNodeContents(textNode);

  // Clamp to the selection range
  if (range.compareBoundaryPoints(Range.START_TO_START, nodeRange) > 0) {
    nodeRange.setStart(range.startContainer, range.startOffset);
  }
  if (range.compareBoundaryPoints(Range.END_TO_END, nodeRange) < 0) {
    nodeRange.setEnd(range.endContainer, range.endOffset);
  }

  const wrapper = doc.createElement(tag);
  const contents = nodeRange.extractContents();
  wrapper.appendChild(contents);
  nodeRange.insertNode(wrapper);
}

function wrapTextNodeRangeWithClass(textNode, range, className, doc) {
  const nodeRange = doc.createRange();
  nodeRange.selectNodeContents(textNode);

  if (range.compareBoundaryPoints(Range.START_TO_START, nodeRange) > 0) {
    nodeRange.setStart(range.startContainer, range.startOffset);
  }
  if (range.compareBoundaryPoints(Range.END_TO_END, nodeRange) < 0) {
    nodeRange.setEnd(range.endContainer, range.endOffset);
  }

  const wrapper = doc.createElement('span');
  wrapper.className = className;
  const contents = nodeRange.extractContents();
  wrapper.appendChild(contents);
  nodeRange.insertNode(wrapper);
}

function isFullyClassWrapped(range, className) {
  const contents = getTextNodesInRange(range);
  if (contents.length === 0) return false;

  for (const textNode of contents) {
    if (!hasAncestorClass(textNode, className, range.commonAncestorContainer)) {
      return false;
    }
  }
  return true;
}

function hasAnyClass(el, classNames) {
  if (!el || !el.classList) return false;
  for (const cls of classNames) {
    if (el.classList.contains(cls)) return true;
  }
  return false;
}

function removeClassesInRangeByPredicate(range, predicate, doc) {
  const container =
    range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;

  const elements = [];
  let ancestor = range.commonAncestorContainer;
  if (ancestor.nodeType === 3) ancestor = ancestor.parentNode;
  while (ancestor) {
    if (ancestor.nodeType === 1 && ancestor.classList) {
      for (const cls of ancestor.classList) {
        if (predicate(cls)) {
          elements.push(ancestor);
          break;
        }
      }
    }
    ancestor = ancestor.parentNode;
  }

  const descendants = container.querySelectorAll ? Array.from(container.querySelectorAll('*')) : [];
  for (const el of descendants) {
    if (!el.classList) continue;
    if (!range.intersectsNode(el)) continue;
    if (elements.indexOf(el) !== -1) continue;
    for (const cls of el.classList) {
      if (predicate(cls)) {
        elements.push(el);
        break;
      }
    }
  }

  for (const el of elements) {
    if (!el || !el.parentNode || !el.classList) continue;
    const toRemove = [];
    for (const cls of el.classList) {
      if (predicate(cls)) toRemove.push(cls);
    }
    for (const cls of toRemove) el.classList.remove(cls);
    if (el.classList.length === 0) {
      el.removeAttribute('class');
    }

    // Unwrap class-less spans, but DO NOT call parent.normalize() — that
    // merges adjacent text nodes and JSDOM/some browsers collapse any
    // ranges whose containers get merged away. Leaving the text node
    // alone (no merging) keeps any caller's Range valid for a follow-up
    // applyClassFormat. Stray empty spans get cleaned up here too.
    if (el.tagName === 'SPAN' && el.attributes.length === 0) {
      const parent = el.parentNode;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
    }
  }
}

function removeClassesInRange(range, classNames, doc) {
  const container =
    range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;

  const elements = [];
  let ancestor = range.commonAncestorContainer;
  if (ancestor.nodeType === 3) ancestor = ancestor.parentNode;
  while (ancestor) {
    if (ancestor.nodeType === 1 && hasAnyClass(ancestor, classNames)) {
      elements.push(ancestor);
    }
    ancestor = ancestor.parentNode;
  }

  const selector = classNames.map((cls) => `.${cls}`).join(',');
  const descendants = container.querySelectorAll
    ? Array.from(container.querySelectorAll(selector))
    : [];
  for (const el of descendants) {
    if (range.intersectsNode(el) && elements.indexOf(el) === -1) {
      elements.push(el);
    }
  }

  for (const el of elements) {
    if (!el || !el.parentNode || !el.classList) continue;
    for (const cls of classNames) {
      el.classList.remove(cls);
    }
    if (el.classList.length === 0) {
      el.removeAttribute('class');
    }

    if (el.tagName === 'SPAN' && el.attributes.length === 0) {
      const parent = el.parentNode;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
    }
  }
}

function unwrapFormat(range, tagUpper, altTag, doc) {
  const container =
    range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;

  // Find all matching elements within or containing the range
  const elements = [];

  // Check ancestors of the range
  let ancestor = range.commonAncestorContainer;
  if (ancestor.nodeType === 3) ancestor = ancestor.parentNode;
  while (ancestor) {
    if (ancestor.nodeType === 1) {
      const name = ancestor.tagName;
      if (name === tagUpper || (altTag && name === altTag)) {
        elements.push(ancestor);
      }
    }
    ancestor = ancestor.parentNode;
  }

  // Check descendants
  const descendants = container.querySelectorAll
    ? Array.from(
        container.querySelectorAll(
          tagUpper.toLowerCase() + (altTag ? ',' + altTag.toLowerCase() : '')
        )
      )
    : [];
  for (const el of descendants) {
    if (range.intersectsNode(el) && elements.indexOf(el) === -1) {
      elements.push(el);
    }
  }

  // Unwrap each element (replace with its children)
  for (const el of elements) {
    const parent = el.parentNode;
    if (!parent) continue;
    while (el.firstChild) {
      parent.insertBefore(el.firstChild, el);
    }
    parent.removeChild(el);
  }
}

function unwrapMarkedFormat(marked, tagUpper, altTag, doc) {
  const { start, end } = marked || {};
  if (!start?.parentNode || !end?.parentNode) return;

  // Find an ancestor of `start` that is the format tag AND fully contains
  // `end` -- this is the case where the formatted region extends beyond the
  // marked selection and needs to be split (e.g. unbolding the middle of a
  // bolded word). If no such ancestor exists, the format element sits
  // between the markers as a sibling/descendant instead (see below).
  let target = start.parentElement;
  while (target && target.nodeType === 1) {
    const isMatch = target.tagName === tagUpper || (altTag && target.tagName === altTag);
    if (isMatch && target.contains(end)) break;
    target = target.parentElement;
  }
  const hasWrapperAncestor = !!(
    target &&
    target.parentNode &&
    (target.tagName === tagUpper || (altTag && target.tagName === altTag))
  );

  const selectedRange = doc.createRange();
  selectedRange.setStartAfter(start);
  selectedRange.setEndBefore(end);
  const selected = selectedRange.extractContents();
  const selectedMatches = selected.querySelectorAll
    ? Array.from(
        selected.querySelectorAll(
          tagUpper.toLowerCase() + (altTag ? ',' + altTag.toLowerCase() : '')
        )
      )
    : [];
  for (const el of selectedMatches.reverse()) {
    const parent = el.parentNode;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  }

  if (!hasWrapperAncestor) {
    // No ancestor wrapper to split. This happens when a prior mutation
    // normalized the selection's Range boundary to an (element, childIndex)
    // form -- markRange's comments then land as siblings of the format
    // element rather than nested inside it (e.g. after restoreMarkedRange
    // reselects using setStartAfter/setEndBefore on markers that get
    // removed). The matching tag(s) were already unwrapped above; just put
    // the content back exactly where the markers were.
    selectedRange.insertNode(selected);
    return;
  }

  const afterRange = doc.createRange();
  afterRange.selectNodeContents(target);
  afterRange.setStartAfter(end);
  const afterContents = afterRange.extractContents();
  const afterClone = target.cloneNode(false);
  afterClone.appendChild(afterContents);

  start.remove();
  end.remove();
  const parent = target.parentNode;
  const reference = target.nextSibling;
  parent.insertBefore(start, reference);
  parent.insertBefore(selected, reference);
  parent.insertBefore(end, reference);
  if (afterClone.childNodes.length) parent.insertBefore(afterClone, reference);
  if (!target.childNodes.length) target.remove();
}

function removeLinksInRange(range, doc) {
  const container =
    range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;

  // Check if an ancestor is a link
  let ancestor = range.commonAncestorContainer;
  if (ancestor.nodeType === 3) ancestor = ancestor.parentNode;
  const parentLink = ancestor.closest ? ancestor.closest('a') : null;

  const links = [];
  if (parentLink) links.push(parentLink);

  const descendants = container.querySelectorAll ? Array.from(container.querySelectorAll('a')) : [];
  for (const a of descendants) {
    if (range.intersectsNode(a) && links.indexOf(a) === -1) {
      links.push(a);
    }
  }

  for (const a of links) {
    const parent = a.parentNode;
    if (!parent) continue;
    while (a.firstChild) {
      parent.insertBefore(a.firstChild, a);
    }
    parent.removeChild(a);
  }
}

/**
 * Generates the format engine as an inline script string for iframe injection.
 * Uses plain function declarations (no ES modules) since it runs inside the iframe.
 */
export function generateFormatEngineScript() {
  return `
(function() {
  'use strict';
  var INLINE_TEXT_COLOR_CLASS_BY_TOKEN = {
    accent: 'pb-inline-color-accent',
    heading: 'pb-inline-color-heading',
    body: 'pb-inline-color-body'
  };
  var INLINE_TEXT_COLOR_TOKENS = ['accent', 'heading', 'body'];
  var INLINE_TEXT_COLOR_CLASSES = [
    INLINE_TEXT_COLOR_CLASS_BY_TOKEN.accent,
    INLINE_TEXT_COLOR_CLASS_BY_TOKEN.heading,
    INLINE_TEXT_COLOR_CLASS_BY_TOKEN.body
  ];

  function getTextNodesInRange(range) {
    var nodes = [];
    var root = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function(node) {
        if (node.textContent.length === 0) return NodeFilter.FILTER_REJECT;
        var nr = document.createRange();
        nr.selectNodeContents(node);
        if (range.compareBoundaryPoints(Range.END_TO_START, nr) >= 0) return NodeFilter.FILTER_REJECT;
        if (range.compareBoundaryPoints(Range.START_TO_END, nr) <= 0) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var n;
    while ((n = walker.nextNode())) nodes.push(n);
    return nodes;
  }

  function hasAncestorTag(node, tagUpper, altTag, boundary) {
    var cur = node.parentNode;
    while (cur) {
      if (cur.nodeType === 1) {
        var name = cur.tagName;
        if (name === tagUpper || (altTag && name === altTag)) return true;
      }
      if (cur === boundary || cur === boundary.parentNode) break;
      cur = cur.parentNode;
    }
    return false;
  }

  function hasAncestorClass(node, className, boundary) {
    var cur = node.parentNode;
    while (cur) {
      if (cur.nodeType === 1 && cur.classList && cur.classList.contains(className)) return true;
      if (cur === boundary || cur === boundary.parentNode) break;
      cur = cur.parentNode;
    }
    return false;
  }

  function isFullyWrapped(range, tagUpper, altTag) {
    var nodes = getTextNodesInRange(range);
    if (nodes.length === 0) return false;
    for (var i = 0; i < nodes.length; i++) {
      if (!hasAncestorTag(nodes[i], tagUpper, altTag, range.commonAncestorContainer)) return false;
    }
    return true;
  }

  function markRange(range) {
    if (!range || range.collapsed) return null;
    var start = document.createComment('pb-format-start');
    var end = document.createComment('pb-format-end');
    var endPoint = range.cloneRange();
    endPoint.collapse(false);
    endPoint.insertNode(end);
    var startPoint = range.cloneRange();
    startPoint.collapse(true);
    startPoint.insertNode(start);
    var workingRange = document.createRange();
    workingRange.setStartAfter(start);
    workingRange.setEndBefore(end);
    return { start: start, end: end, range: workingRange };
  }

  function restoreMarkedRange(marked) {
    if (!marked || !marked.start.parentNode || !marked.end.parentNode) return null;
    var next = document.createRange();
    next.setStartAfter(marked.start);
    next.setEndBefore(marked.end);
    var selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(next);
    }
    marked.start.parentNode.removeChild(marked.start);
    marked.end.parentNode.removeChild(marked.end);
    return selection && selection.rangeCount ? selection.getRangeAt(0) : next;
  }

  function firstTextDescendant(node) {
    var current = node;
    while (current && current.nodeType !== 3) current = current.firstChild;
    return current && current.nodeType === 3 ? current : null;
  }

  function lastTextDescendant(node) {
    var current = node;
    while (current && current.nodeType !== 3) current = current.lastChild;
    return current && current.nodeType === 3 ? current : null;
  }

  function isWordCharacter(char) {
    if (!char) return false;
    return char === '_' || /[0-9]/.test(char) || char.toLocaleLowerCase() !== char.toLocaleUpperCase();
  }

  function expandRangeToWord(range, editableRoot) {
    if (!range || !range.collapsed || !editableRoot) return range;
    var textNode = range.startContainer.nodeType === 3 ? range.startContainer : null;
    var offset = range.startOffset;
    if (!textNode) {
      var container = range.startContainer;
      var childOffset = offset;
      textNode = firstTextDescendant(container.childNodes && container.childNodes[childOffset]);
      offset = 0;
      if (!textNode && childOffset > 0) {
        textNode = lastTextDescendant(container.childNodes && container.childNodes[childOffset - 1]);
        offset = textNode && textNode.nodeValue ? textNode.nodeValue.length : 0;
      }
    }
    if (!textNode || !editableRoot.contains(textNode)) return range;
    var value = textNode.nodeValue || '';
    if (offset === value.length && offset > 0 && isWordCharacter(value[offset - 1])) offset -= 1;
    if (!isWordCharacter(value[offset])) return range;
    var start = offset;
    var end = offset + 1;
    while (start > 0 && isWordCharacter(value[start - 1])) start -= 1;
    while (end < value.length && isWordCharacter(value[end])) end += 1;
    var expanded = range.cloneRange();
    expanded.setStart(textNode, start);
    expanded.setEnd(textNode, end);
    return expanded;
  }

  function wrapTextNodeRange(textNode, range, tag) {
    var nr = document.createRange();
    nr.selectNodeContents(textNode);
    if (range.compareBoundaryPoints(Range.START_TO_START, nr) > 0) {
      nr.setStart(range.startContainer, range.startOffset);
    }
    if (range.compareBoundaryPoints(Range.END_TO_END, nr) < 0) {
      nr.setEnd(range.endContainer, range.endOffset);
    }
    var wrapper = document.createElement(tag);
    var contents = nr.extractContents();
    wrapper.appendChild(contents);
    nr.insertNode(wrapper);
  }

  function wrapTextNodeRangeWithClass(textNode, range, className) {
    var nr = document.createRange();
    nr.selectNodeContents(textNode);
    if (range.compareBoundaryPoints(Range.START_TO_START, nr) > 0) {
      nr.setStart(range.startContainer, range.startOffset);
    }
    if (range.compareBoundaryPoints(Range.END_TO_END, nr) < 0) {
      nr.setEnd(range.endContainer, range.endOffset);
    }
    var wrapper = document.createElement('span');
    wrapper.className = className;
    var contents = nr.extractContents();
    wrapper.appendChild(contents);
    nr.insertNode(wrapper);
  }

  function applyFormat(tag, range) {
    var nodes = getTextNodesInRange(range);
    if (nodes.length === 0) return;
    var tagUpper = tag.toUpperCase();
    var altTag = tagUpper === 'STRONG' ? 'B' : tagUpper === 'EM' ? 'I' : null;
    for (var i = nodes.length - 1; i >= 0; i--) {
      if (!hasAncestorTag(nodes[i], tagUpper, altTag, range.commonAncestorContainer)) {
        wrapTextNodeRange(nodes[i], range, tag);
      }
    }
    mergeEquivalentWrappers(range.commonAncestorContainer, tag);
  }

  function mergeEquivalentWrappers(root, tag) {
    var boundary = root && root.nodeType === 1 ? root : (root ? root.parentElement : null);
    if (!boundary || !boundary.querySelectorAll) return;
    var wrappers = Array.from(boundary.querySelectorAll(tag));
    for (var i = wrappers.length - 1; i >= 0; i--) {
      var wrapper = wrappers[i];
      if (wrapper.parentElement && wrapper.parentElement.tagName === tag.toUpperCase()
          && wrapper.attributes.length === 0) {
        while (wrapper.firstChild) wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
        wrapper.parentNode.removeChild(wrapper);
      }
    }
    wrappers = Array.from(boundary.querySelectorAll(tag));
    for (var j = 0; j < wrappers.length; j++) {
      var current = wrappers[j];
      var next = current.nextSibling;
      while (next && next.nodeType === 3 && next.nodeValue === '') next = next.nextSibling;
      if (next && next.nodeType === 1 && next.tagName === current.tagName
          && current.attributes.length === 0 && next.attributes.length === 0) {
        while (next.firstChild) current.appendChild(next.firstChild);
        next.parentNode.removeChild(next);
      }
    }
  }

  function applyClassFormat(className, range) {
    var nodes = getTextNodesInRange(range);
    if (nodes.length === 0) return;
    for (var i = nodes.length - 1; i >= 0; i--) {
      wrapTextNodeRangeWithClass(nodes[i], range, className);
    }
  }

  function isFullyClassWrapped(range, className) {
    var nodes = getTextNodesInRange(range);
    if (nodes.length === 0) return false;
    for (var i = 0; i < nodes.length; i++) {
      if (!hasAncestorClass(nodes[i], className, range.commonAncestorContainer)) return false;
    }
    return true;
  }

  function hasAnyClass(el, classNames) {
    if (!el || !el.classList) return false;
    for (var i = 0; i < classNames.length; i++) {
      if (el.classList.contains(classNames[i])) return true;
    }
    return false;
  }

  function unwrapFormat(range, tagUpper, altTag) {
    var container = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;
    var elements = [];

    var anc = range.commonAncestorContainer;
    if (anc.nodeType === 3) anc = anc.parentNode;
    while (anc) {
      if (anc.nodeType === 1) {
        var nm = anc.tagName;
        if (nm === tagUpper || (altTag && nm === altTag)) elements.push(anc);
      }
      anc = anc.parentNode;
    }

    var selector = tagUpper.toLowerCase() + (altTag ? ',' + altTag.toLowerCase() : '');
    var desc = container.querySelectorAll ? Array.from(container.querySelectorAll(selector)) : [];
    for (var j = 0; j < desc.length; j++) {
      if (range.intersectsNode(desc[j]) && elements.indexOf(desc[j]) === -1) {
        elements.push(desc[j]);
      }
    }

    for (var k = 0; k < elements.length; k++) {
      var el = elements[k];
      var parent = el.parentNode;
      if (!parent) continue;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    }
  }

  function unwrapMarkedFormat(marked, tagUpper, altTag) {
    var start = marked && marked.start;
    var end = marked && marked.end;
    if (!start || !end || !start.parentNode || !end.parentNode) return;

    // Find an ancestor of start that is the format tag AND fully contains
    // end -- this is the case where the formatted region extends beyond
    // the marked selection and needs to be split (e.g. unbolding the middle
    // of a bolded word). If no such ancestor exists, the format element
    // sits between the markers as a sibling/descendant instead (see below).
    var target = start.parentElement;
    while (target && target.nodeType === 1) {
      var isMatch = target.tagName === tagUpper || (altTag && target.tagName === altTag);
      if (isMatch && target.contains(end)) break;
      target = target.parentElement;
    }
    var hasWrapperAncestor = !!(target && target.parentNode &&
      (target.tagName === tagUpper || (altTag && target.tagName === altTag)));

    var selectedRange = document.createRange();
    selectedRange.setStartAfter(start);
    selectedRange.setEndBefore(end);
    var selected = selectedRange.extractContents();
    var selectedMatches = selected.querySelectorAll
      ? Array.from(selected.querySelectorAll(tagUpper.toLowerCase() + (altTag ? ',' + altTag.toLowerCase() : '')))
      : [];
    for (var i = selectedMatches.length - 1; i >= 0; i--) {
      var el = selectedMatches[i];
      var matchParent = el.parentNode;
      while (el.firstChild) matchParent.insertBefore(el.firstChild, el);
      matchParent.removeChild(el);
    }

    if (!hasWrapperAncestor) {
      // No ancestor wrapper to split. This happens when a prior mutation
      // normalized the selection's Range boundary to an (element,
      // childIndex) form -- markRange's comments then land as siblings of
      // the format element rather than nested inside it (e.g. after
      // restoreMarkedRange reselects using setStartAfter/setEndBefore on
      // markers that get removed). The matching tag(s) were already
      // unwrapped above; just put the content back where the markers were.
      selectedRange.insertNode(selected);
      return;
    }

    var afterRange = document.createRange();
    afterRange.selectNodeContents(target);
    afterRange.setStartAfter(end);
    var afterContents = afterRange.extractContents();
    var afterClone = target.cloneNode(false);
    afterClone.appendChild(afterContents);

    start.parentNode.removeChild(start);
    end.parentNode.removeChild(end);
    var parent = target.parentNode;
    var reference = target.nextSibling;
    parent.insertBefore(start, reference);
    parent.insertBefore(selected, reference);
    parent.insertBefore(end, reference);
    if (afterClone.childNodes.length) parent.insertBefore(afterClone, reference);
    if (!target.childNodes.length) parent.removeChild(target);
  }

  function removeClassesInRange(range, classNames) {
    var container = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;
    var elements = [];

    var anc = range.commonAncestorContainer;
    if (anc.nodeType === 3) anc = anc.parentNode;
    while (anc) {
      if (anc.nodeType === 1 && hasAnyClass(anc, classNames)) elements.push(anc);
      anc = anc.parentNode;
    }

    var selector = classNames.map(function(cls) { return '.' + cls; }).join(',');
    var desc = container.querySelectorAll ? Array.from(container.querySelectorAll(selector)) : [];
    for (var i = 0; i < desc.length; i++) {
      if (range.intersectsNode(desc[i]) && elements.indexOf(desc[i]) === -1) {
        elements.push(desc[i]);
      }
    }

    for (var j = 0; j < elements.length; j++) {
      var el = elements[j];
      if (!el || !el.parentNode || !el.classList) continue;
      for (var k = 0; k < classNames.length; k++) {
        el.classList.remove(classNames[k]);
      }
      if (el.classList.length === 0) el.removeAttribute('class');

      if (el.tagName === 'SPAN' && el.attributes.length === 0) {
        var parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
    }
  }

  function removeClassesInRangeByPredicate(range, predicate) {
    var container = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;
    var elements = [];

    var anc = range.commonAncestorContainer;
    if (anc.nodeType === 3) anc = anc.parentNode;
    while (anc) {
      if (anc.nodeType === 1 && anc.classList) {
        for (var ci = 0; ci < anc.classList.length; ci++) {
          if (predicate(anc.classList[ci])) { elements.push(anc); break; }
        }
      }
      anc = anc.parentNode;
    }

    var desc = container.querySelectorAll ? Array.from(container.querySelectorAll('*')) : [];
    for (var di = 0; di < desc.length; di++) {
      var el = desc[di];
      if (!el.classList || !range.intersectsNode(el) || elements.indexOf(el) !== -1) continue;
      for (var dc = 0; dc < el.classList.length; dc++) {
        if (predicate(el.classList[dc])) { elements.push(el); break; }
      }
    }

    for (var ei = 0; ei < elements.length; ei++) {
      var t = elements[ei];
      if (!t || !t.parentNode || !t.classList) continue;
      var toRemove = [];
      for (var rc = 0; rc < t.classList.length; rc++) {
        if (predicate(t.classList[rc])) toRemove.push(t.classList[rc]);
      }
      for (var rr = 0; rr < toRemove.length; rr++) t.classList.remove(toRemove[rr]);
      if (t.classList.length === 0) t.removeAttribute('class');
      // No parent.normalize() — see the ESM helper for why.
      if (t.tagName === 'SPAN' && t.attributes.length === 0) {
        var parent = t.parentNode;
        while (t.firstChild) parent.insertBefore(t.firstChild, t);
        parent.removeChild(t);
      }
    }
  }

  function _closestFontSlug(node, boundary) {
    var cur = node.parentNode;
    while (cur) {
      if (cur.nodeType === 1 && cur.classList) {
        for (var i = 0; i < cur.classList.length; i++) {
          var cls = cur.classList[i];
          if (cls.indexOf('pb-font-') === 0) return cls.slice('pb-font-'.length);
        }
      }
      if (cur === boundary || cur === boundary.parentNode) break;
      cur = cur.parentNode;
    }
    return null;
  }

  function _pbHasFontClass(el) {
    if (!el || !el.classList) return false;
    for (var i = 0; i < el.classList.length; i++) {
      if (el.classList[i].indexOf('pb-font-') === 0) return true;
    }
    return false;
  }

  function _pbStripFontClasses(el) {
    if (!el || !el.classList) return;
    var rm = [];
    for (var i = 0; i < el.classList.length; i++) {
      if (el.classList[i].indexOf('pb-font-') === 0) rm.push(el.classList[i]);
    }
    for (var j = 0; j < rm.length; j++) el.classList.remove(rm[j]);
    if (el.classList.length === 0) el.removeAttribute('class');
  }

  function _pbUnwrapClasslessSpan(el) {
    if (!el || el.tagName !== 'SPAN' || el.attributes.length !== 0) return;
    var parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  }

  function _pbFindEnclosingFontSpan(range) {
    var node = range.commonAncestorContainer;
    if (node.nodeType === 3) node = node.parentNode;
    while (node) {
      if (node.nodeType === 1 && node.tagName === 'SPAN' && _pbHasFontClass(node)) return node;
      node = node.parentNode;
    }
    return null;
  }

  function _pbStripFontClassesInFragment(fragment) {
    var elements = fragment.querySelectorAll ? Array.from(fragment.querySelectorAll('*')) : [];
    for (var i = elements.length - 1; i >= 0; i--) {
      _pbStripFontClasses(elements[i]);
      _pbUnwrapClasslessSpan(elements[i]);
    }
  }

  function _pbFindDirectMarkedFontAncestor(startMarker, endMarker) {
    var current = startMarker.parentNode;
    while (current && current.nodeType === 1) {
      if (_pbHasFontClass(current)
          && startMarker.parentNode === current
          && endMarker.parentNode === current) {
        return current;
      }
      current = current.parentNode;
    }
    return null;
  }

  function _pbRemoveIfEmptyElement(el) {
    if (!el || !el.parentNode || el.childNodes.length !== 0) return;
    el.parentNode.removeChild(el);
  }

  function _pbLiftMarkedFontContent(startMarker, endMarker) {
    var fontAncestor = _pbFindDirectMarkedFontAncestor(startMarker, endMarker);
    if (!fontAncestor || !fontAncestor.parentNode) {
      if (startMarker.parentNode) startMarker.parentNode.removeChild(startMarker);
      if (endMarker.parentNode) endMarker.parentNode.removeChild(endMarker);
      return;
    }

    var parent = fontAncestor.parentNode;
    var afterClone = fontAncestor.cloneNode(false);
    var node = endMarker.nextSibling;
    while (node) {
      var next = node.nextSibling;
      afterClone.appendChild(node);
      node = next;
    }

    var lifted = document.createDocumentFragment();
    node = startMarker.nextSibling;
    while (node && node !== endMarker) {
      var nextLift = node.nextSibling;
      lifted.appendChild(node);
      node = nextLift;
    }

    if (startMarker.parentNode) startMarker.parentNode.removeChild(startMarker);
    if (endMarker.parentNode) endMarker.parentNode.removeChild(endMarker);

    var beforeNext = fontAncestor.nextSibling;
    if (lifted.childNodes.length) parent.insertBefore(lifted, beforeNext);
    if (afterClone.childNodes.length) parent.insertBefore(afterClone, beforeNext);
    _pbRemoveIfEmptyElement(fontAncestor);
  }

  function findNearestEditableBlock(node, editableRoot) {
    var BLOCK_TAGS = { P:1, H1:1, H2:1, H3:1, H4:1, H5:1, H6:1, LI:1, BLOCKQUOTE:1, PRE:1 };
    var cur = node && node.nodeType === 1 ? node : (node ? node.parentNode : null);
    while (cur && cur !== editableRoot) {
      if (cur.nodeType === 1 && BLOCK_TAGS[cur.tagName]) return cur;
      cur = cur.parentNode;
    }
    return editableRoot && editableRoot.nodeType === 1 ? editableRoot : null;
  }

  function replaceChildrenWith(node, replacement) {
    while (node.firstChild) {
      replacement.appendChild(node.firstChild);
    }
    node.appendChild(replacement);
  }

  function copyAttributes(source, target) {
    var attrs = Array.from(source.attributes || []);
    for (var i = 0; i < attrs.length; i++) {
      target.setAttribute(attrs[i].name, attrs[i].value);
    }
  }

  function replaceElementTag(element, tagName) {
    if (!element || !element.parentNode) return null;
    var desired = String(tagName || '').toUpperCase();
    if (!desired || element.tagName === desired) return element;
    var replacement = document.createElement(desired.toLowerCase());
    copyAttributes(element, replacement);
    while (element.firstChild) replacement.appendChild(element.firstChild);
    element.parentNode.replaceChild(replacement, element);
    return replacement;
  }

  function selectEndOf(element) {
    var selection = window.getSelection();
    if (!selection || !element) return;
    var next = document.createRange();
    next.selectNodeContents(element);
    next.collapse(false);
    selection.removeAllRanges();
    selection.addRange(next);
  }

  function appendListItemFromNode(list, node) {
    var li = document.createElement('li');
    if (node && node.nodeType === 1 && /^(P|H[1-6]|DIV|BLOCKQUOTE)$/.test(node.tagName)) {
      while (node.firstChild) li.appendChild(node.firstChild);
    } else if (node) {
      li.appendChild(node);
    }
    if (!li.childNodes.length) li.appendChild(document.createElement('br'));
    list.appendChild(li);
  }

  function replaceListWithDiv(listRoot) {
    var div = document.createElement('div');
    copyAttributes(listRoot, div);
    var children = Array.from(listRoot.childNodes || []);
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (child.nodeType === 1 && child.tagName === 'LI') {
        var p = document.createElement('p');
        while (child.firstChild) p.appendChild(child.firstChild);
        if (!p.childNodes.length) p.appendChild(document.createElement('br'));
        div.appendChild(p);
      } else {
        div.appendChild(child);
      }
    }
    listRoot.parentNode.replaceChild(div, listRoot);
    return div;
  }

  function liftMarkerToRoot(marker, root) {
    while (marker && marker.parentNode && marker.parentNode !== root) {
      var host = marker.parentNode;
      var parent = host.parentNode;
      if (!parent) return false;
      var after = host.cloneNode(false);
      while (marker.nextSibling) after.appendChild(marker.nextSibling);
      parent.insertBefore(marker, host.nextSibling);
      if ((after.textContent || '').length || (after.querySelector && after.querySelector('*'))) {
        parent.insertBefore(after, marker.nextSibling);
      }
      if (!host.childNodes.length) parent.removeChild(host);
    }
    return !!(marker && marker.parentNode === root);
  }

  window.__pbFormatEngine = {
    toggleFormat: function(tag, range) {
      if (range.collapsed) return false;
      var marked = markRange(range);
      var workingRange = marked ? marked.range : range;
      var tagUpper = tag.toUpperCase();
      var altTag = tagUpper === 'STRONG' ? 'B' : tagUpper === 'EM' ? 'I' : null;
      if (isFullyWrapped(workingRange, tagUpper, altTag)) {
        if (marked) unwrapMarkedFormat(marked, tagUpper, altTag);
        else unwrapFormat(workingRange, tagUpper, altTag);
        restoreMarkedRange(marked);
        return false;
      }
      applyFormat(tag, workingRange);
      restoreMarkedRange(marked);
      return true;
    },

    applyTextColor: function(colorToken, range) {
      if (!range || range.collapsed) return false;
      var className = INLINE_TEXT_COLOR_CLASS_BY_TOKEN[colorToken];
      if (!className) return false;

      var marked = markRange(range);
      var workingRange = marked ? marked.range : range;
      var wasAlreadyActive = isFullyClassWrapped(workingRange, className);
      removeClassesInRange(workingRange, INLINE_TEXT_COLOR_CLASSES);
      if (wasAlreadyActive) {
        restoreMarkedRange(marked);
        return false;
      }

      applyClassFormat(className, workingRange);
      restoreMarkedRange(marked);
      return true;
    },

    applyFontFamily: function(slug, range) {
      if (!range || range.collapsed) return false;
      var rawSlug = String(slug || '').trim().toLowerCase();
      var safeSlug = rawSlug.replace(/[^a-z0-9-]/g, '');
      if (safeSlug !== rawSlug) return false;

      var enclosing = _pbFindEnclosingFontSpan(range);
      if (enclosing && range.toString() === enclosing.textContent) {
        _pbStripFontClasses(enclosing);
        if (safeSlug) {
          enclosing.classList.add('pb-font-' + safeSlug);
          return true;
        }
        _pbUnwrapClasslessSpan(enclosing);
        return false;
      }

      var fragment = range.extractContents();
      if (!fragment || fragment.childNodes.length === 0) return false;
      _pbStripFontClassesInFragment(fragment);

      var startMarker = document.createComment('pb-font-start');
      var endMarker = document.createComment('pb-font-end');
      range.insertNode(endMarker);
      endMarker.parentNode.insertBefore(startMarker, endMarker);

      if (safeSlug) {
        var inserted = document.createElement('span');
        inserted.className = 'pb-font-' + safeSlug;
        inserted.appendChild(fragment);
        endMarker.parentNode.insertBefore(inserted, endMarker);
      } else {
        endMarker.parentNode.insertBefore(fragment, endMarker);
      }

      _pbLiftMarkedFontContent(startMarker, endMarker);
      return !!safeSlug;
    },

    getActiveFontSlug: function(range) {
      if (!range || range.collapsed) return null;
      var nodes = getTextNodesInRange(range);
      if (nodes.length === 0) return null;
      var active = null;
      for (var i = 0; i < nodes.length; i++) {
        var s = _closestFontSlug(nodes[i], range.commonAncestorContainer);
        if (!s) return null;
        if (active == null) active = s;
        else if (active !== s) return null;
      }
      return active;
    },

    applyLink: function(href, range, doc, options) {
      if (range.collapsed) return;
      doc = doc || document;
      options = options || {};
      var openInNewTab = options.openInNewTab !== false;

      var activeNode = range.commonAncestorContainer;
      if (activeNode.nodeType === 3) activeNode = activeNode.parentNode;
      var activeAnchor = activeNode && activeNode.closest ? activeNode.closest('a') : null;
      if (activeAnchor && activeAnchor.contains(range.startContainer) && activeAnchor.contains(range.endContainer)) {
        activeAnchor.setAttribute('href', href);
        if (openInNewTab) {
          activeAnchor.setAttribute('target', '_blank');
          activeAnchor.setAttribute('rel', 'noopener noreferrer');
        } else {
          activeAnchor.removeAttribute('target');
          activeAnchor.removeAttribute('rel');
        }
        var activeSel = window.getSelection();
        activeSel.removeAllRanges();
        var activeRange = doc.createRange();
        activeRange.selectNodeContents(activeAnchor);
        activeSel.addRange(activeRange);
        return;
      }

      var container = range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentNode;
      var parentLink = null;
      var anc = range.commonAncestorContainer;
      if (anc.nodeType === 3) anc = anc.parentNode;
      if (anc.closest) parentLink = anc.closest('a');
      var links = [];
      if (parentLink) links.push(parentLink);
      var descLinks = container.querySelectorAll ? Array.from(container.querySelectorAll('a')) : [];
      for (var i = 0; i < descLinks.length; i++) {
        if (range.intersectsNode(descLinks[i]) && links.indexOf(descLinks[i]) === -1) {
          links.push(descLinks[i]);
        }
      }
      for (var j = 0; j < links.length; j++) {
        var a = links[j]; var p = a.parentNode;
        if (!p) continue;
        while (a.firstChild) p.insertBefore(a.firstChild, a);
        p.removeChild(a);
      }
      // Now wrap in new link
      var fragment = range.extractContents();
      var anchor = doc.createElement('a');
      anchor.href = href;
      if (openInNewTab) {
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
      }
      anchor.appendChild(fragment);
      range.insertNode(anchor);
      var sel = window.getSelection();
      sel.removeAllRanges();
      var nr = doc.createRange();
      nr.selectNodeContents(anchor);
      sel.addRange(nr);
    },

    removeLink: function(range) {
      var container = range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentNode;
      var anc = range.commonAncestorContainer;
      if (anc.nodeType === 3) anc = anc.parentNode;
      var parentLink = anc.closest ? anc.closest('a') : null;
      var links = [];
      if (parentLink) links.push(parentLink);
      var desc = container.querySelectorAll ? Array.from(container.querySelectorAll('a')) : [];
      for (var i = 0; i < desc.length; i++) {
        if (range.intersectsNode(desc[i]) && links.indexOf(desc[i]) === -1) links.push(desc[i]);
      }
      for (var j = 0; j < links.length; j++) {
        var a = links[j]; var p = a.parentNode;
        if (!p) continue;
        while (a.firstChild) p.insertBefore(a.firstChild, a);
        p.removeChild(a);
      }
    },

    getActiveLink: function(range) {
      var node = range.commonAncestorContainer;
      if (node.nodeType === 3) node = node.parentNode;
      var a = node.closest ? node.closest('a') : null;
      return a ? {
        href: a.getAttribute('href') || a.href,
        target: a.getAttribute('target') || '',
        rel: a.getAttribute('rel') || '',
        element: a
      } : null;
    },

    expandRangeToWord: expandRangeToWord,

    getActiveTextColor: function(range) {
      if (!range || range.collapsed) return null;
      for (var i = 0; i < INLINE_TEXT_COLOR_TOKENS.length; i++) {
        var token = INLINE_TEXT_COLOR_TOKENS[i];
        if (isFullyClassWrapped(range, INLINE_TEXT_COLOR_CLASS_BY_TOKEN[token])) {
          return token;
        }
      }
      return null;
    },

    isFormatActive: function(tag, range) {
      var tagUpper = tag.toUpperCase();
      var altTag = tagUpper === 'STRONG' ? 'B' : tagUpper === 'EM' ? 'I' : null;
      return isFullyWrapped(range, tagUpper, altTag);
    },

    convertBlockTag: function(tagName, range, editableRoot) {
      if (!range || !editableRoot) return false;
      var desired = String(tagName || '').toUpperCase();
      if (!/^(P|H[1-6]|BLOCKQUOTE)$/.test(desired)) return false;
      if (editableRoot.tagName === 'UL' || editableRoot.tagName === 'OL') {
        var listMarker = document.createComment('pb-list-block');
        var markerRange = range.cloneRange();
        markerRange.collapse(true);
        markerRange.insertNode(listMarker);
        var listDiv = replaceListWithDiv(editableRoot);
        var listBlock = listMarker.parentElement;
        if (!listBlock || listBlock === listDiv) {
          listMarker.remove();
          return false;
        }
        var listReplacement = document.createElement(desired.toLowerCase());
        copyAttributes(listBlock, listReplacement);
        while (listBlock.firstChild) listReplacement.appendChild(listBlock.firstChild);
        listBlock.parentNode.replaceChild(listReplacement, listBlock);
        listMarker.remove();
        selectEndOf(listReplacement);
        return listDiv;
      }
      var block = findNearestEditableBlock(range.startContainer, editableRoot);
      if (!block) return false;

      if (block === editableRoot) {
        if (editableRoot.tagName === desired) return false;
        var rootReplacement = replaceElementTag(editableRoot, desired);
        selectEndOf(rootReplacement);
        return rootReplacement;
      }

      if (block.tagName === desired) return false;
      var replacement = document.createElement(desired.toLowerCase());
      copyAttributes(block, replacement);
      while (block.firstChild) replacement.appendChild(block.firstChild);
      if (block.parentNode) block.parentNode.replaceChild(replacement, block);
      selectEndOf(replacement);
      return editableRoot;
    },

    toggleBulletList: function(range, editableRoot) {
      if (!range || !editableRoot) return false;
      if (editableRoot.tagName === 'UL' || editableRoot.tagName === 'OL') {
        var div = replaceListWithDiv(editableRoot);
        selectEndOf(div);
        return div;
      }

      var ul = document.createElement('ul');
      copyAttributes(editableRoot, ul);
      var nodes = Array.from(editableRoot.childNodes || []);
      var inlineBucket = [];
      function flushInlineBucket() {
        if (!inlineBucket.length) return;
        var li = document.createElement('li');
        for (var j = 0; j < inlineBucket.length; j++) li.appendChild(inlineBucket[j]);
        ul.appendChild(li);
        inlineBucket = [];
      }
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (node.nodeType === 1 && /^(P|H[1-6]|DIV|BLOCKQUOTE)$/.test(node.tagName)) {
          flushInlineBucket();
          appendListItemFromNode(ul, node);
        } else {
          inlineBucket.push(node);
        }
      }
      flushInlineBucket();
      if (!ul.children.length) appendListItemFromNode(ul, null);
      editableRoot.parentNode.replaceChild(ul, editableRoot);
      selectEndOf(ul.lastElementChild || ul);
      return ul;
    },

    insertDivider: function(range, editableRoot) {
      if (!range || !editableRoot) return false;
      try {
        if (!editableRoot.contains(range.commonAncestorContainer)) return false;
      } catch (e) {
        return false;
      }
      range.deleteContents();
      var marker = document.createComment('pb-divider');
      range.insertNode(marker);
      var root = editableRoot;
      if (root.tagName === 'UL' || root.tagName === 'OL') {
        root = replaceListWithDiv(root);
      } else if (root.tagName !== 'DIV') {
        root = replaceElementTag(root, 'DIV');
      }
      if (!liftMarkerToRoot(marker, root)) return false;
      var hr = document.createElement('hr');
      var spacer = document.createElement('br');
      marker.parentNode.replaceChild(hr, marker);
      hr.parentNode.insertBefore(spacer, hr.nextSibling);
      var next = document.createRange();
      next.setStartAfter(spacer);
      next.collapse(true);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(next);
      return root;
    }
  };
})();
`;
}
